// pageInside/payments/WithdrawMoneyModal.jsx
//
// "Withdraw Money" (Wallet & Banking share this one component and the one
// server withdrawal service). The method choice follows the Family
// switches: bank only / PayPal only -> no choice; both -> choice; none ->
// withdrawals unavailable. Amount, fee and net come from the server quote;
// the confirmed attempt carries ONE idempotency key reused on retry.
import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FaPaypal, FaUniversity } from "react-icons/fa";
import { elakayAutoText } from "../dataElakay";
import {
  getWithdrawalBankAccountsAPI, getPayPalRecipientAPI, quoteWithdrawalAPI, createWithdrawalAPI, newIdempotencyKey,
} from "../../auth/paymentsApi";
import { formatCents, parseAmountToCents, withdrawalMethodState } from "./paymentMethodsLogic";
import { paymentErrorMessage, unavailabilityMessage, withdrawalStatusLabel } from "./walletLabels";

const t = elakayAutoText;

const WithdrawMoneyModal = ({ show, onHide, availableCents = 0, withdrawalMethods = {}, onDone }) => {
  const methodState = useMemo(() => withdrawalMethodState(withdrawalMethods), [withdrawalMethods]);
  const [method, setMethod] = useState(methodState.defaultMethod);
  const [banks, setBanks] = useState({ loading: true, list: [], payoutsEnabled: false, connected: false });
  const [bankId, setBankId] = useState("");
  const [paypal, setPaypal] = useState({ loading: true, recipient: null });
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("form");
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [attemptKey, setAttemptKey] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!show) return;
    setMethod(methodState.defaultMethod);
    setStep("form"); setQuote(null); setError(""); setResult(null); setAmount(""); setAttemptKey("");
    if (withdrawalMethods.bankTransfer) {
      getWithdrawalBankAccountsAPI().then((res) => {
        const list = (res.banks || []).filter((b) => b.status !== "errored" && b.status !== "verification_failed");
        setBanks({ loading: false, list, payoutsEnabled: res.payoutsEnabled, connected: res.connected });
        setBankId((list.find((b) => b.isDefault) || list[0])?.id || "");
      }).catch(() => setBanks({ loading: false, list: [], payoutsEnabled: false, connected: false }));
    }
    if (withdrawalMethods.paypal) {
      getPayPalRecipientAPI().then((res) => setPaypal({ loading: false, recipient: res.recipient })).catch(() => setPaypal({ loading: false, recipient: null }));
    }
  }, [show, withdrawalMethods.bankTransfer, withdrawalMethods.paypal, methodState.defaultMethod]);

  const cents = parseAmountToCents(amount);
  const destinationReady = method === "bank" ? Boolean(bankId) && banks.payoutsEnabled : Boolean(paypal.recipient?.connected);

  const review = async () => {
    setError("");
    if (!cents) { setError(t("h_pay_invalid_amount", "Enter a valid amount.")); return; }
    if (cents > availableCents) { setError(t("h_pay_insufficient_balance", "Insufficient Available Balance")); return; }
    setBusy(true);
    try {
      const res = await quoteWithdrawalAPI({ method, amountCents: cents });
      if (!res.quote.sufficient) {
        const why = unavailabilityMessage(res.quote.reason);
        setError(`${t("h_pay_insufficient_balance", "Insufficient Available Balance")}${why ? ` — ${why}` : ""}`);
        return;
      }
      setQuote(res.quote);
      setAttemptKey(newIdempotencyKey());
      setStep("confirm");
    } catch (e) {
      setError(paymentErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await createWithdrawalAPI({ method, amountCents: quote.amountCents, ...(method === "bank" ? { externalAccountId: bankId } : {}) }, attemptKey);
      setResult({ ...res.withdrawal, replayed: Boolean(res.replayed) });
      setStep("result");
      onDone && onDone(res.withdrawal);
    } catch (e) {
      // A network error keeps the same key: "Confirm" again is a safe retry.
      setError(paymentErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const destinationLabel = method === "bank"
    ? (() => { const b = banks.list.find((x) => x.id === bankId); return b ? `${b.bankName || t("h_pay_bank_account", "Bank Account")} •••• ${b.last4}` : ""; })()
    : paypal.recipient?.maskedEmail || "";

  return (
    <Modal show={show} onHide={onHide} centered aria-labelledby="elk-withdraw-title">
      <Modal.Header closeButton>
        <Modal.Title id="elk-withdraw-title">{t("h_pay_withdraw_money", "Withdraw Money")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="elk-summary-row"><span>{t("h_pay_available_balance", "Available Balance")}</span><strong>{formatCents(availableCents)}</strong></div>

        {methodState.unavailable ? (
          <div className="elk-notice elk-notice--warn" role="alert" style={{ marginTop: 12 }}>{t("h_pay_withdrawals_unavailable", "Withdrawals are temporarily unavailable. Your balance and history remain available.")}</div>
        ) : step === "result" && result ? (
          <div style={{ marginTop: 12 }}>
            <div className={`elk-notice ${result.status === "failed" || result.status === "cancelled" ? "elk-notice--error" : "elk-notice--ok"}`} role="status">
              {withdrawalStatusLabel(result.status)} — {formatCents(result.amountCents)}
            </div>
            {result.replayed && <div className="elk-pay-hint">{t("h_pay_err_already_processed", "Withdrawal request already processed.")}</div>}
            {result.status === "processing" && <div className="elk-pay-hint">{t("h_pay_processing_note", "The provider is processing your withdrawal. It is marked completed only when the provider confirms it.")}</div>}
            {result.status === "failed" && <div className="elk-pay-hint">{result.fundsReleased ? t("h_pay_failed_released", "Nothing was sent: the amount is available again.") : t("h_pay_failed_reconcile", "The provider reported a problem. Elakay is reconciling it; the amount stays reserved.")}</div>}
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            {methodState.showChoice && (
              <div className="elk-radio-row" role="radiogroup" aria-label={t("h_pay_choose_destination", "Choose destination")}>
                <button type="button" role="radio" aria-checked={method === "bank"} className={`elk-pay-methods__option${method === "bank" ? " is-active" : ""}`} onClick={() => { setMethod("bank"); setStep("form"); }}>
                  <FaUniversity aria-hidden="true" /> {t("h_pay_bank_account", "Bank Account")}
                </button>
                <button type="button" role="radio" aria-checked={method === "paypal"} className={`elk-pay-methods__option${method === "paypal" ? " is-active" : ""}`} onClick={() => { setMethod("paypal"); setStep("form"); }}>
                  <FaPaypal aria-hidden="true" /> {t("h_pay_paypal_account", "PayPal Account")}
                </button>
              </div>
            )}

            {method === "bank" && (banks.loading ? <div className="elk-pay-hint">{t("h_pay_loading", "Loading...")}</div> : !banks.connected || !banks.list.length ? (
              <div className="elk-notice elk-notice--warn">{t("h_pay_bank_required", "Add and select a bank account first.")}</div>
            ) : (
              <div className="elk-field">
                <label htmlFor="elk-wd-bank">{t("h_pay_bank_account", "Bank Account")}</label>
                <select id="elk-wd-bank" value={bankId} disabled={step !== "form"} onChange={(e) => setBankId(e.target.value)}>
                  {banks.list.map((b) => <option key={b.id} value={b.id}>{`${b.bankName || "Bank"} •••• ${b.last4} (${String(b.currency).toUpperCase()})`}</option>)}
                </select>
                {!banks.payoutsEnabled && <div className="elk-notice elk-notice--warn">{t("h_pay_account_not_verified", "Your payout account is not verified yet. Complete the Stripe verification first.")}</div>}
              </div>
            ))}

            {method === "paypal" && (paypal.loading ? <div className="elk-pay-hint">{t("h_pay_loading", "Loading...")}</div> : !paypal.recipient?.connected ? (
              <div className="elk-notice elk-notice--warn">{t("h_pay_paypal_required", "Add your PayPal account in Banking first.")}</div>
            ) : (
              <div className="elk-summary-row"><span>{t("h_pay_paypal_recipient", "PayPal recipient")}</span><strong>{paypal.recipient.maskedEmail}</strong></div>
            ))}

            {step === "form" ? (
              <div className="elk-field" style={{ marginTop: 12 }}>
                <label htmlFor="elk-wd-amount">{t("h_pay_amount", "Amount")} (USD)</label>
                <input id="elk-wd-amount" inputMode="decimal" autoComplete="off" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
            ) : (
              <div style={{ marginTop: 12 }}>
                <div className="elk-summary-row"><span>{t("h_pay_destination", "Destination")}</span><strong>{destinationLabel}</strong></div>
                <div className="elk-summary-row"><span>{t("h_pay_amount", "Amount")}</span><strong>{formatCents(quote.amountCents)}</strong></div>
                <div className="elk-summary-row"><span>{t("h_pay_fee", "Fee")}</span><strong>{formatCents(quote.feeCents)}</strong></div>
                <div className="elk-summary-row"><span>{t("h_pay_net_amount", "Net amount")}</span><strong>{formatCents(quote.netCents)}</strong></div>
                <div className="elk-pay-hint">{t("h_pay_fee_note", "Elakay does not charge a withdrawal fee. Your bank may apply its own fees.")}</div>
              </div>
            )}
            {error && <div className="elk-notice elk-notice--error" role="alert">{error}</div>}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === "confirm" && <button type="button" className="elk-btn elk-btn--ghost" disabled={busy} onClick={() => setStep("form")}>{t("h_pay_back", "Back")}</button>}
        {step === "form" && !methodState.unavailable && (
          <button type="button" className="elk-btn elk-btn--primary" disabled={busy || !destinationReady || !cents} onClick={review}>{t("h_pay_review", "Review")}</button>
        )}
        {step === "confirm" && <button type="button" className="elk-btn elk-btn--primary" disabled={busy} onClick={confirm}>{busy ? t("h_pay_processing", "Processing...") : t("h_pay_confirm_withdrawal", "Confirm Withdrawal")}</button>}
        {(step === "result" || methodState.unavailable) && <button type="button" className="elk-btn elk-btn--ghost" onClick={onHide}>{t("h_pay_close", "Close")}</button>}
      </Modal.Footer>
    </Modal>
  );
};

export default WithdrawMoneyModal;
